<template>
  <div :class="`d-flex flex-column align-items-stretch gap-2`">
    <div class="d-flex flex-row w-100">
      <div :class="`d-flex flex-column flex-grow-1`">
        <div class="d-flex flex-row align-items-center">
          <div class="flex-grow-1 me-1">
            <div>{{ normalizedAddress }}</div>
            <AssessorLink :property="property" class="small text-muted" use-end-icon/>
          </div>
        </div>
        <div v-if="normalizedOwners.length > 0" class="small text-muted">
          <div v-for="owner in normalizedOwners" :key="owner">{{ owner }}</div>
        </div>
      </div>
      <b-image
        :src="property.imageUrl"
        style="cursor: pointer"
        :size="86"
        auto-width
        image-class="rounded ms-2"
        @click="expanded = !expanded"
        :class="{'opacity-75':expanded}"
      />
    </div>
    <b-collapse :show="expanded">
      <ScrollerWithBlurMask direction="x" style="height:172px" class="w-100">
        <div class="d-flex flex-row gap-1 h-100">
          <b-image
            v-for="image in property.imageUrls"
            :key="image"
            :src="image"
            :size="172"
            auto-width
            image-class="rounded"
            style="cursor:pointer"
            @click="expanded = false"
          />
        </div>
      </ScrollerWithBlurMask>
    </b-collapse>
    <div class="border rounded p-1 bg-light">
      <div class="small text-muted d-flex flex-row align-items-center gap-1">
        <i class="bi-geo-alt" />
        {{ formatNumber(Math.round(distanceFeet)) }} ft from listing coordinates
      </div>
      <div class="small text-muted d-flex flex-row align-items-center gap-1" v-if="matchHostToPropertyOwner">
        <i class="bi-person" />
        Host matches property owner
      </div>
      <div class="small text-muted d-flex flex-row align-items-center gap-1" v-if="matchHostToOtherHosts">
        <i class="bi-person" />
        Host matches property's other listings
      </div>
      <div v-if="!!license" class="small d-flex flex-row text-muted gap-1 align-items-start">
        <i class="bi-file-earmark-check"/>
        <div>License #{{ license.number }}{{ !licenseOwnerNamesText ? '' : `: ${licenseOwnerNamesText}` }}</div>
      </div>
    </div>
    <div v-if="listings.length > 0" class="d-flex flex-column align-items-stretch rounded bg-gray">
      <div class="list-group">
        <div class="list-group-item small">
          {{ formatNumber(listings.length, 'Other listing') }}
        </div>
        <router-link
          v-for="listing in listings"
          :key="listing.id"
          class="list-group-item list-group-item-action d-flex flex-row align-items-center hover-target position-relative flex-grow-1"
          :to="{name:'listing-detail',params:{cityId:listing.city.id,selectedListingId:listing.id}}"
        >
          <ListingItemView :value="listing" hide-address compact />
          <div class="show-on-hover position-absolute end-0 top-0 bottom-0 m-2">
            <div class="h-100 d-flex flex-column align-items-center justify-content-center">
              <div class="bg-white rounded">
                <icon-button @click.stop :href="safeHref(listing.url)" target="_blank" rel="noopener noreferrer" icon="box-arrow-up-right" />
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
    <div v-if="isVerified && (listing.verifiedByUser?.displayName || listing.dateVerified)" class="p-2 rounded bg-gray small">
      <div>
        Verified
        {{ listing.verifiedByUser?.displayName ? ` by ${listing.verifiedByUser?.displayName}` : '' }}
        {{ listing.dateVerified ? ` on ${formatDate(listing.dateVerified)}` : '' }}
      </div>
      <div v-if="listing.verifiedNotes" class="mt-2 gap-2 d-flex flex-row ">
        <i class="bi-chat-text"/>
        <div>
          {{ listing.verifiedNotes }}
        </div>
      </div>
    </div>
    <div>
      <BCollapse :show="!verifying">
        <div class="d-flex flex-row align-items-center justify-content-end">
          <div class="flex-grow-1 me-2 d-flex flex-row align-items-center" v-if="!verifying">
            <a v-if="!isVerified" class="btn btn-primary btn-sm me-2" @click="verifying = true">Verify</a>
            <a v-else class="btn btn-danger btn-sm me-2" @click="verifying = true"><i class="bi-trash"/> Clear</a>
          </div>
          <IconButton v-if="!hideSync" @click="sync" title="Sync details with assessor DB" icon="arrow-clockwise" :busy="property.isImportingDetails" />
          <IconButton v-if="property.realtorUrl" :href="safeHref(property.realtorUrl)" target="_blank" rel="noopener noreferrer" title="Open in Redfin in a new tab">
            <i class="bi-house"/>
          </IconButton>
          <slot name="buttons" />
        </div>
      </BCollapse>
      <BCollapse :show="verifying">
        <div class="d-flex flex-column align-items-stretch small border rounded shadow-sm p-3 gap-2">
          <div>
            <template v-if="isVerified">
              Are you sure you want to clear this address?
            </template>
            <template v-else>
              Are you sure you want to verify this property?
            </template>
          </div>
          <textarea class="form-control form-control-sm" v-model="notes" placeholder="Notes (optional)" />
          <div class="d-flex flex-row align-items-center justify-content-end mb-3">
            <button type="button" :class="`btn btn-${ isVerified ? 'danger' : 'primary' } btn-sm`" @click.stop="commit" :disabled="listing.verifying">
              <template v-if="listing.verifying">
                <spinner-view small /> {{ isVerified ? 'Clearing' : 'Verifying' }}
              </template>
              <template v-else>
                Yes, {{ isVerified ? 'clear' : 'verify' }} it
              </template>
            </button>
            <button type="button" class="ms-3 btn btn-outline-dark btn-sm" @click.stop="verifying = false">
              Cancel
            </button>
          </div>
        </div>
      </BCollapse>
    </div>
  </div>
</template>

<script>
import { safeHref } from '@/safeRendering';
import {Listing} from "@/models/Listing";
import BCollapse from "@/components/common/BCollapse.vue";
import BImage from "@/components/common/BImage.vue";
import IconButton from "@/components/common/IconButton.vue";
import AssessorLink from "@/components/common/AssessorLink.vue";
import ListingItemView from "@/components/ListingItemView.vue";
import SpinnerView from "@/components/common/SpinnerView.vue";
import ScrollerWithBlurMask from "@/components/common/ScrollerWithBlurMask.vue";
import {PropertyDetails} from "@/models/PropertyDetails";
import { normalizeAddressLines } from "@/addressFormatting";
import { formatDisplayName } from "@/nameFormatting";

export default {
  components: {
    ScrollerWithBlurMask,
    SpinnerView, ListingItemView, AssessorLink, IconButton, BImage, BCollapse
  },
  props: {
    listing: Listing,
    property: PropertyDetails,
    matchHostToPropertyOwner: Boolean,
    matchHostToOtherHosts: Boolean,
    hideSync: Boolean
  },
  data() {
    return {
      verifying: false,
      expanded: false,
      notes: ''
    };
  },
  computed: {
    isVerified() {
      return this.listing.verifiedPropertyId === this.property.id;
    },
    license() {
      return this.property.license;
    },
    listings() {
      return this.property.listings || [];
    },
    normalizedAddress() {
      const rawAddress = this.property.addressFormatted;
      const [line1] = normalizeAddressLines(rawAddress);
      return line1 || rawAddress || 'Unknown address';
    },
    normalizedOwners() {
      const names = [];
      for (const owner of (this.property.owners || [])) {
        const formattedName = formatDisplayName(owner);
        if (!formattedName) continue;
        if (!names.includes(formattedName)) {
          names.push(formattedName);
        }
      }
      return names;
    },
    licenseOwnerNamesText() {
      const names = (this.license?.contact?.owners || [])
      .map(owner => formatDisplayName(owner))
      .filter(Boolean);
      return names.join(', ');
    },
    distanceFeet() {
      const p1 = this.listing.latlng;
      const p2 = this.property.latlng;
      if (!p2) {
        return null;
      }
      // console.log(`Computing distance between ${p1.lat},${p1.lng} and ${p2.lat},${p2.lng}`)
      const R_EARTH = 3958.8;

      const rlat1 = p1.lat * (Math.PI/180); // Convert degrees to radians
      const rlat2 = p2.lat * (Math.PI/180); // Convert degrees to radians
      const difflat = rlat2 - rlat1; // Radian difference (latitudes)
      const difflng = (p2.lng-p1.lng) * (Math.PI/180); // Radian difference (longitudes)

      return Math.round(2 * R_EARTH * Math.asin(
        Math.sqrt(
          Math.sin(difflat/2) * Math.sin(difflat/2) +
          Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflng/2) * Math.sin(difflng/2)
        )) * 5280);
    }
  },
  methods: {
    safeHref,

    async commit() {
      if (this.isVerified) {
        await this.listing.verifyProperty(null, this.notes);
      } else {
        await this.listing.verifyProperty(this.property, this.notes);


        this.$showToast(`
          <div class="d-flex flex-row gap-2 align-items-center">
            ${this.listing.primaryImage ? `<img src="${this.listing.primaryImage}" class="v-toast__image"/>` : ''}
              <div class="d-flex flex-column gap-0">
                <div class="fw-bold">${this.listing.listingTitle}</div>
                <div>Verified at ${this.normalizedAddress}</div>
              </div>
            </div>
        `);
      }
    },
    sync() {
      this.property.importDetails();
    }
  }
}
</script>
